// Per-user cache of the getUserBusinesses() payload so the app can render the
// last-known company immediately on open while the fresh list is fetched in
// the background (stale-while-revalidate).
//
// This cache is a rendering hint only — it grants nothing. Every API request
// is authorized server-side per call (CompanyVerificationGuard re-checks
// company membership in Firestore), so a revoked business in the cache can't
// return any data; the background refresh then reconciles the UI. Keys are
// scoped by Firebase UID so one user's list can never render for another
// account on the same device, and all keys are cleared on logout.

const KEY_PREFIX = 'riw-company-list-cache:v1:';

const keyFor = (uid: string) => `${KEY_PREFIX}${uid}`;

export const readCompanyListCache = (uid: string | undefined): any[] | null => {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(keyFor(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed;
  } catch (err) {
    try {
      localStorage.removeItem(keyFor(uid));
    } catch (removeErr) {}
    return null;
  }
};

export const writeCompanyListCache = (
  uid: string | undefined,
  data: any[]
): void => {
  if (!uid || !Array.isArray(data)) return;
  try {
    localStorage.setItem(keyFor(uid), JSON.stringify(data));
  } catch (err) {
    // Quota exceeded or storage unavailable — the app just loads the
    // old (blocking) way next time.
  }
};

export const clearCompanyListCache = (uid: string | undefined): void => {
  if (!uid) return;
  try {
    localStorage.removeItem(keyFor(uid));
  } catch (err) {}
};

export const clearCompanyListCaches = (): void => {
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(KEY_PREFIX)) stale.push(key);
    }
    stale.forEach((key) => localStorage.removeItem(key));
  } catch (err) {}
};
